import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { downloadExcel } from "@src/utils/download-helper";

import {
  GetAllPurchasesAPIParams,
  SummaryType,
  UsePurchasesSummaryServiceOperators
} from "@src/lib/types/purchases";

import {
  allPurchasesRecentAPI,
  allPurchasesSummaryAPI,
  totalPurchaseSummary
} from "@src/api/purchases";
import { usePurchasesContext } from "../provider";
import { TABLE_HEAD_PURCHASE_SUMMARY } from "@src/utils/mockdata/table-headers";
import numeral from "numeral";
import { getCurrentMonthDays } from "@src/utils/current-month";

export const useSummaryService = (): Readonly<UsePurchasesSummaryServiceOperators> => {
  const location = useLocation();
  const { state, handlePageChange } = usePurchasesContext();

  const {
    page,
    fromdate,
    todate,
    order,
    orderBy,
    rowsPerPage,
    userid,
    status,
    paymentOption,
  } = state || {};
  const newPage = (page || 0) + 1;
  const filterUserID = userid;

  const purchasesListQueryKey = [
    "purchases-summary",
    {
      fromdate,
      todate,
      pathname: location.pathname
    },
  ];

  const { data: summary, isLoading, isRefetching, refetch } = useQuery({
    queryKey: purchasesListQueryKey,
    queryFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        startDate: startDate,
        endDate: endDate,
      };

      if (status) reqParams.status = status;
      if (fromdate) reqParams.startDate = fromdate;
      if (todate) reqParams.endDate = todate;

      const res = await allPurchasesSummaryAPI(reqParams);
      return res;
    },
  });

  const { data: recent, } = useQuery({
    queryKey: [
    "purchases-recents",
    {
      newPage,
      fromdate,
      todate,
      order,
      orderBy,
      userid: filterUserID,
      rowsPerPage,
      status,
      paymentOption,
      pathname: location.pathname
    },
  ],
    queryFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        size: rowsPerPage,
        page: newPage,
        startDate: startDate,
        endDate: endDate,
      };

      if (filterUserID) reqParams.search = filterUserID;
      if (status) reqParams.status = status;
      if (fromdate) reqParams.startDate = fromdate;
      if (todate) reqParams.endDate = todate;

      const res = await allPurchasesRecentAPI(reqParams);
      return res;
    },
  });

  const onExportPurchasesList = useMutation({
    mutationKey: ["export-purchases-summary"],
    mutationFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        startDate: startDate,
        endDate: endDate,
      };

      if (status) reqParams.status = status;
      if (fromdate) reqParams.startDate = fromdate;
      if (todate) reqParams.endDate = todate;

      let res = await allPurchasesSummaryAPI(reqParams);

      const newJson = res.items.map((item: SummaryType) => {
        const result: Record<string, any> = {};
          const headers = TABLE_HEAD_PURCHASE_SUMMARY;

        headers.forEach(({ id, label = "" }) => {
          let fieldValue: any;

          if (id === 'fundType') fieldValue = item.fundType;
          if (id === 'transactionCount') fieldValue = numeral(item.transactionCount || 0).format("0,000");
          if (id === 'totalAmount') fieldValue = numeral(item.totalAmount || 0).format("0,000");
          if (id === 'mostPurchasedItem') fieldValue = item.mostPurchasedItem;
          if (id === 'successCount') fieldValue = numeral(item.successCount || 0).format("0,000");
          if (id === 'failureCount') fieldValue = numeral(item.failureCount || 0).format("0,000");
          result[label] = fieldValue;
        });

        return result;
      });

      downloadExcel(newJson, `export-${location.pathname.substring(location.pathname.lastIndexOf('/') + 1)}`);
    },
  });

  const { data: totalPurchase, refetch: refetchTotalPurchase } = useQuery({
    queryKey: ["total-purchases-summary",
      {
        fromdate,
        todate,
        pathname: location.pathname
      }
    ],
    queryFn: async () => {
      const { fromDate: startDate, toDate: endDate } = getCurrentMonthDays();
      const reqParams: GetAllPurchasesAPIParams = {
        startDate: startDate,
        endDate: endDate,
      };

      if (fromdate) reqParams.startDate = fromdate;
      if (todate) reqParams.endDate = todate;

      const purchaseRes = await totalPurchaseSummary(reqParams);

      return purchaseRes;
    },
  });

  const onRefreshPurchases = () => {
    handlePageChange(null, 0);
    refetch();
    refetchTotalPurchase();
  };

  return {
    data: recent,
    summaries: summary?.items || [],
    recents: recent?.items || [],
    isLoading,
    isRefreshing: isRefetching,
    totalPurchase,
    onRefreshPurchases,
    onExportPurchasesList,
  };
};
